import { Card, CardButtonActionMode, HeaderGradient, MiniCard, Row } from '@app/components';
import React, { memo } from 'react';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { StyleSheet } from 'react-native';
import { IStep } from '@app/common';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store';
import SvgInfo from '@app/components/icons/SvgInfo';
import { SlantTitle } from '@app/components/typography';

type StepCardProps = {
  step?: Partial<IStep>;
  toggle: (mode: CardButtonActionMode) => void;
};

const formatCardData = (partialStep: Partial<IStep>) => {
  return {
    name: partialStep?.name || 'Nouvelle étape'
  }
}

export const StepCard = memo(({ toggle, step }: StepCardProps) => {
  const { subContainerHeight } = useDynamicSizes();

  const partialStep = useAppSelector<Partial<IStep>>(triptychSelectors.selectData);
  const cardData = useAppSelector<Partial<IStep>>(triptychSelectors.selectCardData);

  const { name } = formatCardData(cardData ?? partialStep)

  return (
    <Card>

      <HeaderGradient>
        <LinearGradient
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
          colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
          style={styles.headerGradient}
        />
      </HeaderGradient>

      <Card.TopHeader>
        <Card.TopHeaderLabel>Fiche étape</Card.TopHeaderLabel>
      </Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>{name}</Card.Title>
                {step?._id && <Card.Subtitle>Ref: {step._id}</Card.Subtitle>}
              </Card.HeaderTitles>

              <Card.ButtonAction onPress={toggle} permissionScope="steps" />
            </Row>

          </Card.Header>
        </Card.Content>

        <MiniCard>
          <MiniCard.Tabs>
            <MiniCard.TabsHolder>
              <MiniCard.Tab Icon={SvgInfo} isActive />
            </MiniCard.TabsHolder>
          </MiniCard.Tabs>
          <MiniCard.Container>
            <MiniCard.Content>
              <SlantTitle label="Aide" color={theme.colors.gold} />

              <Card.HelpScrollContainer>
                <Card.LineBreak height={16} />

                <Card.HelpTitle>Fonctionnement des étapes</Card.HelpTitle>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Les étapes correspondent aux différentes composantes de votre menu.
                </Card.HelpText>

                <Card.LineBreak height={8} />

                <Card.HelpText>Exemple : </Card.HelpText>
                <Card.HelpExample>- Choix de l’entrée</Card.HelpExample>
                <Card.HelpExample>- Choix du plat</Card.HelpExample>
                <Card.HelpExample>- Choix du dessert</Card.HelpExample>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Une étape peut être facultative ou obligatoire.
                </Card.HelpText>

                <Card.HelpText>
                  Vous pouvez également définir le nombre maximum de produits sélectionnables pour chaque étape.
                </Card.HelpText>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Pensez à utiliser les positions pour classer vos étapes (par exemple : le choix du burger avant le choix de la boisson).
                </Card.HelpText>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Enfin, lorsque vous sélectionnez les produits qui composent une étape, vous pouvez ajouter un prix si celui-ci entraîne un supplément.
                </Card.HelpText>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Pour suivre le tutoriel complet, rendez-vous{' '}
                  <Card.HelpLink href="https://tickeat.featurebase.app/en/help/articles/0503741-creer-un-menu-sur-tickeat">
                    ici
                  </Card.HelpLink>
                </Card.HelpText>

                <Card.LineBreak height={72} />

              </Card.HelpScrollContainer>
            </MiniCard.Content>
          </MiniCard.Container>
        </MiniCard>

      </Card.Container>
    </Card>
  );
});

const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});
